// features/admin/reportListApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface ReportFormat {
  id: number;
  format_name: string;
  report_type: string;
  fields: string[];
  date_range: string;
  export_type: string;
  status: number;
  created_at?: string;
  updated_at?: string;
}

export interface ReportApiResponse<T = any> {
  error: boolean;
  message?: string;
  data?: T;
}

export interface AddReportFormatRequest {
  name: string;
  report_type: string;
  fields: string[];
  date_range?: string;
  export_type?: string;
  status?: number;
  description?: string;
}

export interface UpdateReportFormatRequest {
  id: number;
  name?: string;
  report_type?: string;
  fields?: string[];
  date_range?: string;
  export_type?: string;
  status?: number;
}

export interface DeleteReportFormatRequest {
  id: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const REPORT_TYPE_OPTIONS = [
  'Sales Report',
  'Order Report',
  'User Report',
  'Inventory Report',
  'Payment Report',
  'Credit Report',
  'Wallet Report',
  'Return Report',
];

export const DATE_RANGE_OPTIONS = [
  'Today',
  'Yesterday',
  'This Week',
  'Last Week',
  'This Month',
  'Last Month',
  'This Quarter',
  'This Year',
  'Custom',
];

export const EXPORT_TYPE_OPTIONS = ['PDF', 'Excel', 'CSV'];

export const AVAILABLE_FIELDS: Record<string, string[]> = {
  'Sales Report':     ['order_id', 'user_name', 'total_amount', 'discount', 'tax', 'net_amount', 'payment_method', 'order_date'],
  'Order Report':     ['order_id', 'invoice_no', 'user_name', 'items', 'status', 'total_amount', 'order_date', 'delivery_date'],
  'User Report':      ['user_id', 'full_name', 'email', 'contact', 'member_type', 'registration_date', 'last_order'],
  'Inventory Report': ['product_id', 'product_name', 'sku', 'stock', 'price', 'category', 'last_updated'],
  'Payment Report':   ['transaction_id', 'user_name', 'amount', 'method', 'status', 'payment_date'],
  'Credit Report':    ['user_id', 'user_name', 'credit_limit', 'balance', 'utilized', 'orders_count', 'repayments_count'],
  'Wallet Report':    ['user_id', 'user_name', 'balance', 'total_credited', 'total_debited', 'last_transaction'],
  'Return Report':    ['return_id', 'order_id', 'user_name', 'items', 'reason', 'status', 'refund_amount', 'return_date'],
};

// ─── Config ───────────────────────────────────────────────────────────────────

export const REPORT_API_CONFIG = {
  BASE_URL: process.env.REACT_APP_SERVER_ENDPOINT,
  ENDPOINTS: {
    LIST:   '/admin/list-report-formats',
    ADD:    '/admin/add-report-format',
    UPDATE: '/admin/update-report-format',
    DELETE: '/admin/delete-report-format',
  },
};

// ─── API Slice ────────────────────────────────────────────────────────────────

export const reportListApi = createApi({
  reducerPath: 'reportListApi',
  baseQuery: fetchBaseQuery({
    baseUrl: REPORT_API_CONFIG.BASE_URL,
    prepareHeaders: (headers) => {
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      const token = localStorage.getItem('admin_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['ReportFormats'],
  endpoints: (build) => ({

    listReportFormats: build.query<ReportApiResponse<ReportFormat[]>, void>({
      query: () => ({ url: REPORT_API_CONFIG.ENDPOINTS.LIST, method: 'POST', body: {} }),
      providesTags: ['ReportFormats'],
      transformResponse: (response: any): ReportApiResponse<ReportFormat[]> => {
        const formats: ReportFormat[] = (response?.data || []).map((f: any) => ({
          ...f,
          fields: typeof f.fields === 'string'
            ? (() => { try { return JSON.parse(f.fields); } catch { return []; } })()
            : (f.fields || []),
        }));
        return { error: response?.error ?? false, message: response?.message || '', data: formats };
      },
      transformErrorResponse: (response: any) => ({
        error: true, message: response.data?.message || 'Failed to load report formats', data: [],
      }),
    }),

    addReportFormat: build.mutation<ReportApiResponse<ReportFormat>, AddReportFormatRequest>({
      query: (body) => ({ url: REPORT_API_CONFIG.ENDPOINTS.ADD, method: 'POST', body }),
      invalidatesTags: ['ReportFormats'],
      transformResponse: (response: any) => ({
        error: response?.error ?? false, message: response?.message || 'Created successfully', data: response?.data,
      }),
      transformErrorResponse: (response: any) => ({
        error: true, message: response.data?.message || 'Failed to create report format',
      }),
    }),

    updateReportFormat: build.mutation<ReportApiResponse<ReportFormat>, UpdateReportFormatRequest>({
      query: (body) => ({ url: REPORT_API_CONFIG.ENDPOINTS.UPDATE, method: 'POST', body }),
      invalidatesTags: ['ReportFormats'],
      transformResponse: (response: any) => ({
        error: response?.error ?? false, message: response?.message || 'Updated successfully', data: response?.data,
      }),
      transformErrorResponse: (response: any) => ({
        error: true, message: response.data?.message || 'Failed to update report format',
      }),
    }),

    deleteReportFormat: build.mutation<ReportApiResponse, DeleteReportFormatRequest>({
      query: (body) => ({ url: REPORT_API_CONFIG.ENDPOINTS.DELETE, method: 'POST', body }),
      invalidatesTags: ['ReportFormats'],
      transformResponse: (response: any) => ({
        error: response?.error ?? false, message: response?.message || 'Deleted successfully',
      }),
      transformErrorResponse: (response: any) => ({
        error: true, message: response.data?.message || 'Failed to delete report format',
      }),
    }),

  }),
});

export const {
  useListReportFormatsQuery,
  useAddReportFormatMutation,
  useUpdateReportFormatMutation,
  useDeleteReportFormatMutation,
} = reportListApi;